import React, { forwardRef, useEffect, useState } from 'react'

import { Box, styled, FilledInput as MuiFilledInput, type FilledInputProps as MuiFilledInputProps } from '@mui/material'

import { FIELD_HEIGHT, fieldBorderRest, fieldFocusSx, fieldSurfaceSx } from './fieldMetrics'
import { FieldLabel } from './FieldLabel'
import { space } from 'settings/tokens'

const StyledMuiFilledInput = styled(MuiFilledInput)<MuiFilledInputProps>(({ theme, disabled, multiline }) => ({
  /*
   * The ONE field surface — radius, border and fill — from `fieldSurfaceSx`,
   * not a private copy of the same three properties.
   *
   * This declared them itself: `getFieldRadius(...)` (correct, but a second
   * caller of the clamp), `1px solid grey[300]` and `#f6f6f6`. The border
   * happened to LOOK right only because `index.css`'s `#vera-shadow-root *` rule
   * beats it on ID specificity and repaints every non-`!important` border to
   * `border.subtle`; the authored grey[300] never rendered. The fill was a
   * genuine 1-unit divergence from `grey[50]` (#f5f5f5), which is what
   * `FieldTrigger` and every picker surface use.
   *
   * So the base field and the field-shaped trigger beside it now come from one
   * source and cannot drift. `FIELD_RADIUS_MAX` is why the radius is clamped
   * rather than taken verbatim from the tenant: radius does not scale with
   * width, so the value that makes a 44px button look intentional turns a
   * 343px-wide field into a lozenge.
   */
  ...fieldSurfaceSx(theme),
  marginTop: 0,
  color: theme.palette.text.primary,
  // VERA-304 round 2: 16px is the iOS Safari auto-zoom floor -- any focused
  // form control under 16px triggers a forced pinch-zoom on tap. Was 14.
  // (VERA-291 independently reached this same floor for FieldRow.tsx / PhoneField.tsx.)
  fontSize: 16,
  // VERA-304 verify-walkthrough feedback: a FIXED height clamped every multiline
  // (textarea) usage to look no taller than a one-line input, defeating
  // `minRows`/`maxRows`. Multiline gets a natural, content-driven height instead
  // (MUI's TextareaAutosize handles the grow); single-line keeps the shared
  // FIELD_HEIGHT so it sits level with PhoneField / SelectDrawer / SortField.
  ...(multiline ? { minHeight: FIELD_HEIGHT } : { height: FIELD_HEIGHT }),
  /*
   * `space.lg` (16), matching `FieldTrigger`'s `px: 2`. It was 14, which is MUI's
   * own FilledInput value carried over — so the base field's text started 2px
   * left of the identical-looking trigger's. Invisible in isolation and obvious
   * where they overlap: `CertificatesSearchPicker` renders the trigger and then
   * opens a popover with the field directly on top of it, so the magnifier
   * stepped sideways as it opened.
   *
   * The root owns the whole inset and the input contributes none horizontally —
   * stacking the two is what previously put field text 26px in. The vertical 0
   * also drops the root's 25px floating-label reserve, which on a textarea
   * pushed the placeholder off the first line.
   */
  padding: `0 ${space.lg}px`,
  /*
   * Adornment treatment, owned HERE rather than at each call site.
   *
   * A leading glyph in a field wants the same two things every time — muted, and
   * `space.sm` off the text, exactly as `FieldTrigger` renders its icon — and
   * before this every caller supplied them by hand (`SelectDrawer` and
   * `EditFieldModal` both wrote `sx={{ color: …, mr: 1 }}`, and the five picker
   * search boxes each hand-rolled their own).
   *
   * `svg + .MuiFilledInput-input` is the discriminator, and it has to be: a lone
   * start adornment is both `:first-of-type` and `:last-of-type`, so those
   * cannot tell a leading glyph from a trailing one. "An svg immediately before
   * the input" can only be a START adornment. It also cannot fire on the sigil
   * adornments — TransferOwnership's `'$'` and CreateAccountEmail's `'@'` — which
   * are text, not svg, and MUST stay tight against the value they prefix.
   */
  '& > svg': {
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  '& > svg + .MuiFilledInput-input': {
    marginLeft: `${space.sm}px`,
  },
  // NOTE: MUI's FilledInput renders the editable element as an `<input>` OR a
  // `<textarea>` (when `multiline`), both carrying the SAME
  // `.MuiFilledInput-input` class — but a bare `input` tag selector only ever
  // matches the former, so the textarea path silently got NONE of this
  // padding/placeholder styling. Target the class so both share one look.
  '.MuiFilledInput-input': {
    padding: '8px 0',
    color: theme.palette.text.primary,
    // Explicit -- form controls don't inherit font-size from an ancestor by
    // default (the UA stylesheet declares its own value directly on
    // input/textarea), so setting it only on the styled root doesn't
    // reliably reach the actual `.MuiFilledInput-input` element.
    fontSize: 16,
    // A single-line input inherits a tall line-height to vertically centre its
    // text in the FIELD_HEIGHT box. A textarea must NOT: a full-height line box
    // centres the placeholder inside its own line instead of seating it on the
    // first line, and it makes every typed row that tall. Multiline gets normal
    // leading.
    ...(multiline ? { lineHeight: 1.5 } : {}),
    '::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  /*
   * Hover + focus, from the ONE shared ladder — and, as of 2026-09-02, actually
   * rendering.
   *
   * These two rules existed here for a long time and were DEAD. They wrote a
   * bare `border: 1px solid <accent>` with no `!important`, and `index.css`'s
   * `#vera-shadow-root * { border-color: hsl(var(--border)) }` carries ID
   * specificity (1-0-0), which beats every emotion class rule (0-1-0 / 0-2-0).
   * MEASURED before this change on `/sign-in`: focusing the email field left the
   * border at `rgb(228,228,231)` — the rest colour — with `box-shadow: none` and
   * `outline-style: none`. The field had NO focus indicator of any kind, which
   * is a WCAG 2.2 2.4.7 failure, not a polish gap.
   *
   * `fieldFocusSx` carries the `!important` inside the shorthand, which is the
   * documented escape hatch and the only form that works — a separate
   * `borderColor`, `!important` or not, still loses to the ID selector.
   */
  ...fieldFocusSx(theme),
  '&:hover': {
    // Spread first, then override, because a bare `'&:hover'` key REPLACES the
    // one the spread just contributed rather than merging into it.
    ...fieldFocusSx(theme)['&:hover'],
    // A disabled field gets no hover response at all: its border stays at rest
    // and its fill stays `fieldSurfaceSx`'s `grey[50]`.
    ...(disabled ? { border: `1px solid ${fieldBorderRest(theme)} !important` } : {}),
    // VERA-842: hover is a SLIGHT tint off the resting fill, not a jump to
    // full white. It should read as 'you're on this field', not as a different
    // component. The disabled branch is the RESTING fill (`fieldSurfaceSx`'s
    // `grey[50]`), i.e. no hover response at all — it was a hardcoded `#f6f6f6`
    // that no longer matches the surface it is supposed to leave untouched.
    backgroundColor: disabled ? theme.palette.grey[50] : '#f0f0f0',
  },
  // NO `&.Mui-error` HERE ON PURPOSE — it would not render. `index.css`'s
  // `#vera-shadow-root * { border-color: … }` has ID specificity and beats every
  // emotion class rule in this app, so a border colour written here is authored and
  // then discarded. The error border is applied from the app THEME instead —
  // `settings/mui/MuiInputBase.ts`, with the `!important` that rule needs — which has
  // the further advantage of covering `@repo/ui`'s `Input` in this app too, not just
  // this local copy. See the long note there.
  opacity: disabled ? 0.3 : 1,
}))

export interface InputProps {
  label?: string
  required?: boolean
  placeholder?: string
  defaultValue?: any
  type?: string
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  error?: boolean
  fullWidth?: boolean
  disabled?: boolean
  minRows?: number
  maxRows?: number
  multiline?: boolean
  sx?: object
  inputProps?: MuiFilledInputProps['inputProps']
  onChange?: (value: string) => void
  value?: string
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  autoFocus?: boolean
  name?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  required = false,
  fullWidth = true,
  placeholder,
  startAdornment,
  endAdornment,
  error,
  disabled = false,
  defaultValue = '',
  type = 'text',
  minRows = 2,
  maxRows = 10,
  multiline = false,
  sx,
  inputProps,
  onChange = () => {},
  value,
  onBlur,
  onKeyDown,
  autoFocus,
  name,
}, ref) => {
  const [internalValue, setInternalValue] = useState(value)

  useEffect(() => {
    if (value !== undefined && value !== internalValue) {
      setInternalValue(value)
    }
  }, [value, internalValue])

  const handleOnChange = (e: any) => {
    setInternalValue(e.target.value)
    onChange(e.target.value)
  }

  return (
    /* A FIELD IS ONE ELEMENT IN THE LAYOUT (working-conventions §31).
       This returned a FRAGMENT, so in the `<Stack spacing={n}>` every form in
       this app is built from, the label and the control were two SEPARATE
       children of that Stack. The Stack then injected ITS gap between them and
       `FieldLabel`'s `mb: '8px'` computed to 0 — the canonical 8px label→control
       gap silently became whatever spacing the form happened to choose (on
       Signup's `spacing={2}`, 16px — apps/app is on MUI's 8px base, §29).
       Wrapping makes the field ONE flex child: the parent's spacing now
       separates FIELDS, and label→control belongs to `FieldLabel` alone.
       Same fix, same reason, as `@repo/ui`'s `Input`, which wraps in a `Box`.

       `width` tracks `fullWidth` so the wrapper occupies exactly the space the
       control used to as a flex item. In a column `Stack` that is a no-op
       (align-items: stretch already gave it full width); it matters in the one
       ROW-flex call site — TransferOwnership forces `.MuiFormControl-root` to
       `flex-direction: row` for its "Sale price" field — where an auto-width
       wrapper would resize the control it contains. */
    <Box sx={{ width: fullWidth ? '100%' : undefined, minWidth: 0 }}>
      {/* `top: 12` was a no-op here: the label is statically positioned, so `top`
          never applied and the label sat flush against the field. 8px matches the
          @repo/ui FieldLabel gap, so app and platform fields read the same.

          VERA-315: that gap is no longer restated here. This was a THIRD label
          treatment in the app (a bare `body2` at `mb: 2` — which on apps/app's
          8px base is 16px, double the canonical 8, so a field labelled through
          this prop sat further from its control than the identical field
          labelled with `FieldLabel` beside it; ViewSettingsDrawer rendered both
          on one drawer). §31 says the label treatment has ONE source, so this
          path delegates to it rather than approximating it. */}
      {label && <FieldLabel label={label} />}
      <StyledMuiFilledInput
        ref={ref}
        type={type}
        name={name}
        required={required}
        fullWidth={fullWidth}
        disableUnderline
        placeholder={placeholder}
        multiline={multiline}
        minRows={minRows}
        maxRows={maxRows}
        startAdornment={startAdornment}
        endAdornment={endAdornment}
        sx={sx}
        disabled={disabled}
        error={Boolean(error)}
        inputProps={inputProps}
        onChange={handleOnChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        autoFocus={autoFocus}
        {...(value !== undefined ? { value: internalValue ?? '' } : { defaultValue })}
      />
    </Box>
  )
})


